import React from 'react';
import { useTranslation } from 'react-i18next';
import V2Select from './V2Select';
import '../admin/admin-ui.css';

export type AdminColumn<T> = {
  key: string;
  title: React.ReactNode;
  render: (row: T) => React.ReactNode;
  className?: string;
  sort?: 'ascending' | 'descending' | 'none';
};

type PaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  disabled?: boolean;
};

export function AdminPagination({
  page,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  disabled = false,
}: PaginationProps) {
  const { t } = useTranslation('translation');
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const pages = Array.from(new Set([1, current - 1, current, current + 1, pageCount]))
    .filter((value) => value >= 1 && value <= pageCount)
    .sort((a, b) => a - b);

  if (total <= pageSize && !onPageSizeChange) return null;

  return (
    <nav className="v2-admin-pagination" aria-label={t('admin_ui_pagination')}>
      <span className="v2-admin-pagination__total">
        {t('admin_ui_rows', {
          from: total ? (current - 1) * pageSize + 1 : 0,
          to: Math.min(current * pageSize, total),
          total,
        })}
      </span>
      <div className="v2-admin-pagination__pages">
        <button
          type="button"
          className="v2-admin-page-button"
          disabled={disabled || current === 1}
          aria-label={t('admin_ui_previous')}
          onClick={() => onChange(current - 1)}
        >
          ←
        </button>
        {pages.map((value, index) => (
          <React.Fragment key={value}>
            {index > 0 && value - pages[index - 1] > 1 ? <span aria-hidden="true">…</span> : null}
            <button
              type="button"
              className="v2-admin-page-button"
              disabled={disabled}
              aria-label={t('admin_ui_page', { page: value })}
              aria-current={value === current ? 'page' : undefined}
              onClick={() => onChange(value)}
            >
              {value}
            </button>
          </React.Fragment>
        ))}
        <button
          type="button"
          className="v2-admin-page-button"
          disabled={disabled || current === pageCount}
          aria-label={t('admin_ui_next')}
          onClick={() => onChange(current + 1)}
        >
          →
        </button>
      </div>
      {onPageSizeChange ? (
        <label className="v2-admin-pagination__size">
          {t('admin_ui_page_size')}
          <V2Select
            ariaLabel={t('admin_ui_page_size')}
            value={pageSize}
            disabled={disabled}
            onChange={onPageSizeChange}
            options={Array.from(new Set([20, 50, 100, pageSize]))
              .sort((a, b) => a - b)
              .map((size) => ({ value: size, label: String(size) }))}
          />
        </label>
      ) : null}
    </nav>
  );
}

export function AdminLoading({ label }: { label?: string }) {
  const { t } = useTranslation('translation');
  return (
    <p className="v2-admin-loading" role="status">
      <span aria-hidden="true" />
      {label ?? t('admin_ui_loading')}
    </p>
  );
}

export function AdminNotice({
  type = 'info',
  children,
  action,
}: {
  type?: 'info' | 'error' | 'success';
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      className={`v2-admin-notice v2-admin-notice--${type}`}
      role={type === 'error' ? 'alert' : 'status'}
    >
      <div>{children}</div>
      {action}
    </div>
  );
}

export function AdminTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyText,
  minWidth,
  caption,
  pagination,
}: {
  columns: AdminColumn<T>[];
  rows: readonly T[];
  rowKey: keyof T | ((row: T) => React.Key);
  loading?: boolean;
  emptyText?: React.ReactNode;
  minWidth?: number;
  caption?: string;
  pagination?: PaginationProps;
}) {
  const { t } = useTranslation('translation');
  return (
    <div className="v2-admin-data" aria-busy={loading}>
      {loading ? <AdminLoading /> : null}
      <div
        className="v2-admin-data__scroll"
        tabIndex={0}
        role="region"
        aria-label={caption ?? t('admin_ui_table')}
      >
        <table className="v2-admin-data-table" style={minWidth ? { minWidth } : undefined}>
          {caption ? <caption className="v2-admin-sr-only">{caption}</caption> : null}
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={column.className}
                  aria-sort={column.sort}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={typeof rowKey === 'function' ? rowKey(row) : String(row[rowKey])}>
                {columns.map((column) => (
                  <td key={column.key} className={column.className}>
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && !rows.length ? (
              <tr>
                <td colSpan={columns.length} className="v2-admin-data-table__empty">
                  {emptyText ?? t('admin_ui_empty')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {pagination ? (
        <AdminPagination {...pagination} disabled={loading || pagination.disabled} />
      ) : null}
    </div>
  );
}

export function AdminDialog({
  open,
  title,
  onClose,
  children,
  footer,
  className = '',
}: {
  open: boolean;
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}) {
  const { t } = useTranslation('translation');
  const dialogRef = React.useRef<HTMLDialogElement>(null);
  const titleId = React.useId();
  React.useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className={`v2-admin-dialog ${className}`}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header className="v2-admin-dialog__header">
        <h2 id={titleId}>{title}</h2>
        <button
          type="button"
          className="v2-admin-page-button"
          aria-label={t('admin_ui_close')}
          onClick={onClose}
        >
          ×
        </button>
      </header>
      <div className="v2-admin-dialog__content">{children}</div>
      {footer ? <footer className="v2-admin-dialog__footer">{footer}</footer> : null}
    </dialog>
  );
}

export function AdminConfirmButton({
  children,
  title,
  onConfirm,
  disabled = false,
  className = 'v2-admin-link-danger',
  confirmLabel,
  cancelLabel,
}: {
  children: React.ReactNode;
  title: React.ReactNode;
  onConfirm: () => void;
  disabled?: boolean;
  className?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}) {
  const { t } = useTranslation('translation');
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button type="button" className={className} disabled={disabled} onClick={() => setOpen(true)}>
        {children}
      </button>
      <AdminDialog
        open={open}
        className="v2-admin-dialog--confirm"
        title={t('admin_ui_confirm')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="outline-button" onClick={() => setOpen(false)}>
              {cancelLabel ?? t('cancel')}
            </button>
            <button
              type="button"
              className="v2-admin-button v2-admin-button--danger"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onConfirm();
              }}
            >
              {confirmLabel ?? t('delete')}
            </button>
          </>
        }
      >
        <p>{title}</p>
      </AdminDialog>
    </>
  );
}
